import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../components/context/AuthContext";


const couleurEnAnglais = {
    "rouge": "red",
    "bleu": "blue",
    "vert": "green",
    "jaune": "yellow",
    "noir": "black",
    "violet": "purple"
};

function traduireCouleur(couleur) {
    return couleur && typeof couleur === "string" ? couleurEnAnglais[couleur.toLowerCase()] || "black" : "black";
}

function ModifierMur() {
    const { user } = useAuth();
    const { murId } = useParams();
    const [mur, setMur] = useState(null);
    const [formes, setFormes] = useState([]);
    const [formesDisponibles, setFormesDisponibles] = useState([]);
    const murCanvasRef = useRef(null);
    const formesCanvasRef = useRef(null);
    const [positionsFormes, setPositionsFormes] = useState({});
    const [selectedForme, setSelectedForme] = useState(null);
    const [lastSelectedForme, setLastSelectedForme] = useState(null);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const grilleSize = 50;

    const getApiUrl = () => {
        if (user?.role === "ADMIN") {
          return process.env.REACT_APP_API_ADMIN_URL;
        }
        return process.env.REACT_APP_API_USER_URL;
      };
      const baseUrl = getApiUrl();

    // Charger les formes disponibles
    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_ADMIN_URL}/formes`)
                .then((res) => res.json())
                .then((data) => {
                    const formesAvecPosition = data.map((f, index) => ({
                        ...f,
                        posX: 50,
                        posY: 50 + index * 50,
                    }));
                    setFormes(formesAvecPosition);
                })
                .catch((err) => console.error("Erreur API : ", err));
        }, []);
    
    useEffect(() => {
        // Charger les formes disponibles (utilisées dans les deux canvas)
        fetch(`${process.env.REACT_APP_API_ADMIN_URL}/formes`)
            .then((res) => res.json())
            .then(setFormesDisponibles)
            .catch((err) => console.error("Erreur API Formes :", err));
    }, []); //  Exécuté UNE SEULE FOIS au chargement
    
    useEffect(() => {
        if (!murId || formesDisponibles.length === 0) return;
    
        fetch(`${baseUrl}/MurDeBrique/${murId}`)
            .then((res) => res.json())
            .then((murData) => {
                if (!murData || !murData.briques) {
                    console.warn("Mur vide ou mal formaté :", murData);
                    return;
                }
    
                const briquesAvecDetails = murData.briques.map((brique, index) => {
                    const details = formesDisponibles.find((f) => f.id === brique.formId);
                    if (!details) {
                        console.warn(`Aucune correspondance trouvée pour l'ID ${brique.formId}`);
                        return null;
                    }
                    return {
                        ...details,
                        posX: brique.posX,
                        posY: brique.posY,
                        murFormeId: brique.id,
                        uniqueId: `${brique.id}-${index}` //  Crée une clé unique
                    };
                }).filter(Boolean);
    
                setMur({ ...murData, briques: briquesAvecDetails });
    
                //  Stocker les positions avec un identifiant unique
                const positions = {};
                briquesAvecDetails.forEach((brique) => {
                    positions[brique.uniqueId] = { posX: brique.posX, posY: brique.posY };
                });
                setPositionsFormes(positions);
            })
            .catch((err) => console.error("Erreur API Mur :", err));
    }, [murId, formesDisponibles]);

    useEffect(() => {
    }, [mur]);
    
    useEffect(() => {
    }, [formesDisponibles]);

    // Dessiner la grille
    const dessinerGrille = (ctx, width, height) => {
        ctx.strokeStyle = "#ccc";
        ctx.lineWidth = 0.5;
        for (let x = 0; x <= width; x += grilleSize) {
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, height);
            ctx.stroke();
        }
        for (let y = 0; y <= height; y += grilleSize) {
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(width, y);
            ctx.stroke();
        }
    };

    // Dessiner les formes
    const dessinerFormes = useCallback((canvasRef, listeFormes) => {
        if (!listeFormes || listeFormes.length === 0) return;

        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        dessinerGrille(ctx, canvas.width, canvas.height);

        listeFormes.forEach((forme, index) => {
            if (!forme || !forme.nom) {
                console.warn(` Forme invalide à l'index ${index}`, forme);
                return;
            }
            const couleur = traduireCouleur(forme.couleur);
            ctx.fillStyle = couleur;
            ctx.save();
        
            if (forme.nom.toLowerCase().includes("rectangle")) {
                ctx.fillRect(forme.posX, forme.posY, forme.largeur, forme.longueur);
            } else if (forme.nom.toLowerCase().includes("rond")) {
                ctx.beginPath();
                ctx.arc(forme.posX, forme.posY, forme.rayon, 0, Math.PI * 2);
                ctx.fill();
            } else if (forme.nom.toLowerCase().includes("triangle")) {
                ctx.beginPath();
                ctx.moveTo(forme.posX, forme.posY);
                ctx.lineTo(forme.posX + forme.base, forme.posY);
                ctx.lineTo(forme.posX, forme.posY - forme.hauteur);
                ctx.closePath();
                ctx.fill();
            } else if (forme.nom.toLowerCase().includes("hexagone")) {
                const cote = forme.cotes || 10;
                const posX = forme.posX || 0;
                const posY = forme.posY || 0;
                ctx.beginPath();
                for (let i = 0; i < 6; i++) {
                    const angle = (Math.PI / 3) * i;
                    const x = posX + Math.cos(angle) * cote;
                    const y = posY + Math.sin(angle) * cote;
                    if (i === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.closePath();
                ctx.fill();
            }

            ctx.restore();
        });
    }, []);

    useEffect(() => {
        if (mur) dessinerFormes(murCanvasRef, mur.briques);
    }, [mur, dessinerFormes]);

    useEffect(() => {
        dessinerFormes(formesCanvasRef, formes);
    }, [formes, dessinerFormes]);

    // Ajout d'une forme au mur
    const ajouterFormeAuMur = (forme) => {
        const uniqueId = `new-${Date.now()}`; // Identifiant temporaire
        const nouvelleForme = {
            ...forme,
            posX: 100,
            posY: 100,
            isNew: true, // Important
            uniqueId
        };
        setMur((prev) => ({
            ...prev,
            briques: [...prev.briques, nouvelleForme]
        }));
    };

    const handleMouseDown = (event) => {
        if (!mur || !mur.briques) return; // Vérifier que le mur est bien chargé
    
        const rect = murCanvasRef.current.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
    
        const clickedForme = mur.briques.find((forme) => {
            const nomForme = forme.nom.split(" ")[0];
    
            if (nomForme.toLowerCase().includes("rectangle")) {
                return (
                    mouseX >= forme.posX &&
                    mouseX <= forme.posX + forme.largeur &&
                    mouseY >= forme.posY &&
                    mouseY <= forme.posY + forme.longueur
                );
            } else if (nomForme.toLowerCase().includes("rond")) {
                const distance = Math.sqrt((mouseX - forme.posX) ** 2 + (mouseY - forme.posY) ** 2);
                return distance <= forme.rayon;
            } else if (nomForme.toLowerCase().includes("triangle")) {
                const hauteurAbs = Math.abs(forme.hauteur);
                const baseAbs = Math.abs(forme.base);
                return (
                    mouseX >= forme.posX - baseAbs / 2 &&
                    mouseX <= forme.posX + baseAbs / 2 &&
                    mouseY >= forme.posY - hauteurAbs / 2 &&
                    mouseY <= forme.posY + hauteurAbs / 2
                );
            } else if (nomForme.toLowerCase().includes("hexagone")) {
                const distance = Math.sqrt((mouseX - forme.posX) ** 2 + (mouseY - forme.posY) ** 2);
                return distance <= forme.cotes * 1.5;
            }
            return false;
        });
    
        if (clickedForme) {
            setSelectedForme(clickedForme);
            setLastSelectedForme(clickedForme);
            console.log(lastSelectedForme);
        setOffset({ x: mouseX - clickedForme.posX, y: mouseY - clickedForme.posY });
    }
    };
    const handleMouseMove = (event) => {
        if (!selectedForme || !mur) return;
    
        const rect = murCanvasRef.current.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        const newX = mouseX - offset.x;
    const newY = mouseY - offset.y;

        setSelectedForme(prev => ({
            ...prev,
            posX: newX,
            posY: newY,
        }));
    
        setMur((prevMur) => ({
            ...prevMur,
            briques: prevMur.briques.map((forme) =>
                forme.uniqueId === selectedForme.uniqueId
                    ? { ...forme, posX: mouseX - offset.x, posY: mouseY - offset.y }
                    : forme
            ),
        }));
    };
    const handleMouseUp = () => {
        if (!selectedForme) return;
    
        if (selectedForme.isNew) {
            //  Nouvelle forme → POST
            const payload = {
                murId: parseInt(murId),
                formId: selectedForme.id,
                posX: Number(selectedForme.posX) + 0.0,
                posY: Number(selectedForme.posY) + 0.0
            };
    
            fetch(`${baseUrl}/MurDeBrique/ajouter-forme`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            })
            .then((res) => {
                if (!res.ok) throw new Error("Erreur lors de l'ajout de la nouvelle forme.");
                return res.json();
            })
            .then((data) => {
                console.log("Forme ajoutée avec succès :", data);
    
                //  Met à jour la forme ajoutée avec l'ID retourné
                setMur((prev) => ({
                    ...prev,
                    briques: prev.briques.map((brique) =>
                        brique.uniqueId === selectedForme.uniqueId
                            ? {
                                ...brique,
                                id: data.formId, // ou data.id selon ta réponse
                                murFormeId: data.id, // ← ID de MurForme
                                isNew: false,
                            }
                            : brique
                    )
                }));
            })
            .catch((err) => console.error("Erreur API (ajout forme) :", err));
        } else {
            //  Forme existante → PUT
            const payload = {
                nouvellePosX: Number(selectedForme.posX),
                nouvellePosY: Number(selectedForme.posY),
            };
    
            console.log("Modification position forme existante :", payload);
    
            fetch(`${baseUrl}/MurDeBrique/modifier-forme/${selectedForme.murFormeId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            })
            .then((res) => {
                if (!res.ok) throw new Error("Erreur lors de la mise à jour de la position.");
                console.log(` Position mise à jour pour la forme ID ${selectedForme.id}`);
            })
            .catch((error) => console.error("Erreur API (mise à jour position) :", error));
        }
        setSelectedForme(null);
    };

    const supprimerFormeSelectionnee = () => {
        if (!lastSelectedForme) return;
    
        // 1. Supprimer localement dans tous les cas
        setMur((prev) => ({
            ...prev,
            briques: prev.briques.filter((f) => f.uniqueId !== lastSelectedForme.uniqueId),
        }));
        setLastSelectedForme(null);
    
        // 2. Si la forme est nouvelle mais a déjà été enregistrée côté serveur
        if (!lastSelectedForme.isNew || lastSelectedForme.murFormeId) {
            const murFormeId = lastSelectedForme.murFormeId;
            fetch(`${baseUrl}/MurDeBrique/supprimer-formes/${murFormeId}`, {
                method: "DELETE",
            })
            .then((res) => {
                if (!res.ok) throw new Error("Erreur lors de la suppression.");
                console.log("Forme supprimée du mur (côté serveur)");
            })
            .catch((err) => console.error("Erreur API (suppression forme) :", err));
        } else {
            console.warn("La forme était en attente d'ajout et n'a pas encore reçu de murFormeId.");
        }
    };
    

      return (
        <div className="container">
        <h2>&#128736; Modifier Mur</h2>
        <div className="triple-canvas-container">
            <div className="canvas-panel">
                <canvas
                    ref={murCanvasRef}
                    width={1000}
                    height={500}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                />
            </div>
    
            <div className="formes-panel">
                <canvas
                    ref={formesCanvasRef}
                    width={100}
                    height={500}
                />
            </div>
    
            <div className="boutons-formes">
                {formesDisponibles.map((forme) => (
                    <button key={forme.id} onClick={() => ajouterFormeAuMur(forme)}>
                        &#10133; Ajouter {forme.nom}
                    </button>
                ))}
                <button onClick={supprimerFormeSelectionnee}>
                &#128465;&#65039; Supprimer la forme
                </button>
            </div>
        </div>
    </div>
        );
}

export default ModifierMur;